import { Bus, Home, ArrowRight, ShieldCheck, BadgeCheck, MessageCircle, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const FEATURES = [
  {
    icon: Bus,
    title: 'Venda ⇄ Vaal trips',
    text: 'Safe, affordable student transport between home and campus.',
  },
  {
    icon: Home,
    title: 'Student accommodation',
    text: 'NSFAS-accredited residences close to campus.',
  },
  {
    icon: MapPin,
    title: 'VUT, NWU & Sedibeng',
    text: 'Places within walking distance or with transport to campus.',
  },
];

export default function Hero({ handleTransport, handleBookNow }) {
  const navigate = useNavigate();

  const handleAccommodation = () => {
    navigate('/accommodation'); // navigate to accommodation page
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-orange-800 via-yellow-500 to-orange-400 px-4 pb-20 pt-28">
      {/* Decorative shapes */}
      <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-yellow-200 opacity-20 mix-blend-multiply" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-orange-900 opacity-20 mix-blend-multiply" />
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-white opacity-10 blur-3xl" />

      <div className="container relative z-10 mx-auto max-w-6xl text-center">
        {/* Badge */}
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-4 py-1.5 text-sm font-medium text-white backdrop-blur">
          <Bus className="h-4 w-4 text-yellow-200" />
          Venda to Vaal · Student transport & accommodation
        </div>

        {/* Heading: one h1 that includes the keywords, styled like before */}
        <h1 className="mb-5 drop-shadow-lg">
          <span className="block text-6xl font-extrabold tracking-tight text-white md:text-7xl">V2V</span>
          <span className="mt-3 block text-2xl font-semibold text-yellow-200 md:text-3xl">
            Reliable Student Transport & Accommodation
          </span>
        </h1>

        <p className="mb-4 text-xl font-semibold italic text-white/95 drop-shadow-sm md:text-2xl">
          “Vha khou tuwa afho?”
        </p>

        <p className="mx-auto mb-10 max-w-2xl text-lg text-white/90 drop-shadow-sm md:text-xl">
          V2V provides safe, affordable transport for students travelling from Venda to Vaal, and helps you find
          student accommodation near campus.
        </p>

        {/* Buttons */}
        <div className="flex flex-col justify-center gap-4 sm:flex-row">
          <button
            onClick={handleBookNow}
            className="group inline-flex items-center justify-center gap-2 rounded-xl bg-orange-700 px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-orange-900/30 transition hover:-translate-y-0.5 hover:bg-orange-800"
          >
            <Bus className="h-5 w-5" />
            Book Now
            <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
          </button>

          <button
            onClick={handleAccommodation}
            className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-yellow-400 bg-yellow-400 px-8 py-4 text-lg font-semibold text-orange-900 shadow-lg shadow-orange-900/20 transition hover:-translate-y-0.5 hover:bg-yellow-300"
          >
            <Home className="h-5 w-5" />
            View Accommodation
          </button>
        </div>

        {/* Trust row */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-medium text-white/90">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 text-yellow-200" />
            Safe & reliable
          </span>
          <span className="inline-flex items-center gap-1.5">
            <BadgeCheck className="h-4 w-4 text-yellow-200" />
            NSFAS-accredited residences
          </span>
          <span className="inline-flex items-center gap-1.5">
            <MessageCircle className="h-4 w-4 text-yellow-200" />
            Book easily on WhatsApp
          </span>
        </div>

        {/* Feature cards */}
        <div className="mx-auto mt-14 grid max-w-5xl gap-4 text-left sm:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, text }) => (
            <div
              key={title}
              className="rounded-2xl border border-white/25 bg-white/15 p-5 backdrop-blur-md transition hover:bg-white/20"
            >
              <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-yellow-400">
                <Icon className="h-5 w-5 text-orange-900" />
              </div>
              <h2 className="font-semibold text-white">{title}</h2>
              <p className="mt-1 text-sm text-white/85">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}